import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { TranscriptCaption } from 'features/voice-meeting/interfaces';
import {
  voiceSessionFinished,
  voiceSessionReset,
  voiceSessionTeamingLeaveReset,
} from 'features/voice-session/store';

export interface CaptionLine extends TranscriptCaption {
  receivedAtMs: number;
}

export interface CallCaptionsState {
  lines: CaptionLine[];
  captionsEnabled: boolean;
  // Voice session ids we successfully called start-captions for (Redux-owned bookkeeping).
  startedSessionIds: string[];
  trimOffsets: Record<string, number>;
}

export const initialCallCaptionsState: CallCaptionsState = {
  lines: [],
  captionsEnabled: false,
  startedSessionIds: [],
  trimOffsets: {},
};

const handleVoiceSessionEndSync = (state: CallCaptionsState) => {
  state.lines = [];
  state.startedSessionIds = [];
  state.trimOffsets = {};
  // Keep captionsEnabled — user preference survives reconnect within the same call.
};

const callCaptionsSlice = createSlice({
  name: 'callCaptions',
  initialState: initialCallCaptionsState,
  reducers: {
    captionReceived: {
      reducer: (state, action: PayloadAction<CaptionLine>) => {
        const incomingCaption = action.payload;
        const existingCaptionIndex = state.lines.findIndex(
          (caption) => caption.resultId === incomingCaption.resultId
        );

        if (existingCaptionIndex >= 0) {
          state.lines[existingCaptionIndex] = {
            ...incomingCaption,
            receivedAtMs: state.lines[existingCaptionIndex].receivedAtMs,
          };
          return;
        }

        state.lines.push(incomingCaption);
      },
      prepare: (caption: TranscriptCaption) => ({
        payload: {
          ...caption,
          receivedAtMs: Date.now(),
        },
      }),
    },
    pruneLines: (state, action: PayloadAction<string[]>) => {
      const keepIds = new Set(action.payload);
      state.lines = state.lines.filter((line) => keepIds.has(line.resultId));

      for (const turnKey of Object.keys(state.trimOffsets ?? {})) {
        if (!keepIds.has(turnKey)) {
          delete state.trimOffsets[turnKey];
        }
      }
    },
    incrementCaptionTrim: (
      state,
      action: PayloadAction<{ turnKey: string; charCount: number }>
    ) => {
      const { turnKey, charCount } = action.payload;

      if (!state.trimOffsets) {
        state.trimOffsets = {};
      }

      state.trimOffsets[turnKey] =
        (state.trimOffsets[turnKey] ?? 0) + charCount;
    },
    clearCaptions: (state) => {
      state.lines = [];
      state.trimOffsets = {};
    },
    resetCallCaptions: () => initialCallCaptionsState,
    // User preference during the call; distinct from resetCallCaptions on full call reset.
    setCaptionsEnabled: (state, action: PayloadAction<boolean>) => {
      state.captionsEnabled = action.payload;
    },
    markCaptionsSessionStarted: (state, action: PayloadAction<string>) => {
      if (!state.startedSessionIds.includes(action.payload)) {
        state.startedSessionIds.push(action.payload);
      }
    },
    unmarkCaptionsSessionStarted: (state, action: PayloadAction<string>) => {
      state.startedSessionIds = state.startedSessionIds.filter(
        (id) => id !== action.payload
      );
    },
    clearStartedCaptionsSessions: (state) => {
      state.startedSessionIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(voiceSessionFinished, handleVoiceSessionEndSync)
      .addCase(voiceSessionReset, handleVoiceSessionEndSync)
      .addCase(voiceSessionTeamingLeaveReset, handleVoiceSessionEndSync);
  },
});

export const {
  captionReceived,
  pruneLines,
  incrementCaptionTrim,
  clearCaptions,
  resetCallCaptions,
  setCaptionsEnabled,
  markCaptionsSessionStarted,
  unmarkCaptionsSessionStarted,
  clearStartedCaptionsSessions,
} = callCaptionsSlice.actions;

export const callCaptionsReducer = callCaptionsSlice.reducer;
